import { useSyncExternalStore } from 'react'
import { Howl, Howler } from 'howler'

import { SOUNDS, type SoundDefinition, type SoundId } from '@/src/game/audio/sounds'

const DEFAULT_VOLUME = 1
const TOTAL_SOUND_COUNT = Object.keys(SOUNDS).length

type SoundPreloadSnapshot = {
    started: boolean
    ready: boolean
    loaded: number
    total: number
    progress: number
}

function clampVolume(volume: number): number {
    return Math.min(Math.max(volume, 0), 1)
}

function normalizeSrc(src: SoundDefinition['src']): string[] {
    return typeof src === 'string' ? [src] : [...src]
}

const MUSIC_FADE_MS = 500

const initialSoundPreloadSnapshot: SoundPreloadSnapshot = {
    started: false,
    ready: TOTAL_SOUND_COUNT === 0,
    loaded: 0,
    total: TOTAL_SOUND_COUNT,
    progress: TOTAL_SOUND_COUNT === 0 ? 100 : 0,
}

type PlayMusicOptions = {
    rate?: number
    fadeMs?: number
}

class SoundPlayer {
    private readonly howls = new Map<SoundId, Howl>()
    private muted = false
    private unlockListenersAttached = false

    private soundPreloadStarted = false
    private soundPreloadSnapshot: SoundPreloadSnapshot = initialSoundPreloadSnapshot
    private readonly soundPreloadListeners = new Set<() => void>()
    private readonly finishedPreloadSounds = new Set<SoundId>()

    // Music subsystem: only one track audible at a time, transitions always faded.
    private activeMusicId: SoundId | null = null
    /** Music to resume after a non-looping interruption (e.g. star collect) ends. */
    private suspendedMusicId: SoundId | null = null
    /** Music paused by a temporary overlay (e.g. pause menu) — distinct from suspended. */
    private pausedMusicId: SoundId | null = null
    private readonly musicInstanceIds = new Map<SoundId, number>()
    private readonly musicRates = new Map<SoundId, number>()
    private readonly musicFadeTimers = new Map<SoundId, number>()

    init(): void {
        this.attachUnlockListeners()
    }

    setMuted(muted: boolean): void {
        this.muted = muted
        Howler.mute(muted)
    }

    isMuted(): boolean {
        return this.muted
    }

    getSoundPreloadSnapshot(): SoundPreloadSnapshot {
        return this.soundPreloadSnapshot
    }

    subscribeToSoundPreload(listener: () => void): () => void {
        this.soundPreloadListeners.add(listener)
        return () => {
            this.soundPreloadListeners.delete(listener)
        }
    }

    startSoundPreload(): void {
        if (this.soundPreloadStarted) {
            return
        }

        this.soundPreloadStarted = true
        this.emitSoundPreloadChange()

        for (const soundId of Object.keys(SOUNDS) as SoundId[]) {
            const howl = this.getHowl(soundId)

            if (!howl) {
                this.markSoundPreloadFinished(soundId)
                continue
            }

            if (howl.state() === 'loaded') {
                this.markSoundPreloadFinished(soundId)
                continue
            }

            howl.once('load', () => {
                this.markSoundPreloadFinished(soundId)
            })
            howl.once('loaderror', () => {
                this.markSoundPreloadFinished(soundId)
            })
            howl.load()
        }
    }

    playSound(soundId: SoundId): number | null {
        if (this.muted) {
            return null
        }

        const howl = this.getHowl(soundId)
        if (!howl) {
            return null
        }

        return howl.play()
    }

    playSoundWithVolume(soundId: SoundId, volume: number): number | null {
        if (this.muted) {
            return null
        }

        const howl = this.getHowl(soundId)
        if (!howl) {
            return null
        }

        const instanceId = howl.play()
        howl.volume(clampVolume(volume), instanceId)
        return instanceId
    }

    playSoundWithRate(
        soundId: SoundId,
        rate: number,
        volume: number = DEFAULT_VOLUME,
    ): number | null {
        if (this.muted) {
            return null
        }

        const howl = this.getHowl(soundId)
        if (!howl) {
            return null
        }

        const instanceId = howl.play()
        howl.rate(Math.max(rate, 0.1), instanceId)
        howl.volume(clampVolume(volume), instanceId)
        return instanceId
    }

    playSoundUntilFinished(
        soundId: SoundId,
        volume: number = DEFAULT_VOLUME,
    ): Promise<void> {
        if (this.muted) {
            return Promise.resolve()
        }

        const howl = this.getHowl(soundId)
        if (!howl) {
            return Promise.resolve()
        }

        return new Promise((resolve) => {
            const instanceId = howl.play()
            howl.volume(clampVolume(volume), instanceId)

            const finish = () => resolve()

            howl.once('end', finish, instanceId)
            howl.once('stop', finish, instanceId)
            howl.once('playerror', finish, instanceId)
        })
    }

    stopSound(soundId: SoundId, instanceId?: number): void {
        const howl = this.howls.get(soundId)
        if (!howl) {
            return
        }

        if (instanceId === undefined) {
            howl.stop()
            return
        }

        howl.stop(instanceId)
    }

    stopAll(): void {
        Howler.stop()
        this.activeMusicId = null
        this.suspendedMusicId = null
        this.pausedMusicId = null
        this.musicInstanceIds.clear()
        this.musicRates.clear()
        this.musicFadeTimers.forEach((timerId) => window.clearTimeout(timerId))
        this.musicFadeTimers.clear()
    }

    playMusic(soundId: SoundId, { rate = 1, fadeMs = MUSIC_FADE_MS }: PlayMusicOptions = {}): void {
        this.musicRates.set(soundId, rate)

        if (this.activeMusicId === soundId) {
            this.clearMusicFadeTimer(soundId)
            const howl = this.howls.get(soundId)
            const instanceId = this.musicInstanceIds.get(soundId)
            if (howl && instanceId !== undefined && howl.playing(instanceId)) {
                howl.rate(Math.max(rate, 0.1), instanceId)
            } else {
                this.fadeInMusic(soundId, fadeMs, instanceId !== undefined)
            }
            this.suspendedMusicId = null
            this.pausedMusicId = null
            return
        }

        if (this.activeMusicId) {
            this.fadeOutMusic(this.activeMusicId, fadeMs, 'stop')
        }

        this.suspendedMusicId = null
        this.pausedMusicId = null
        this.fadeInMusic(soundId, fadeMs, false)
        this.activeMusicId = soundId
    }

    pauseMusicWithOverlay(overlaySoundId: SoundId, { fadeMs = MUSIC_FADE_MS }: PlayMusicOptions = {}): void {
        if (this.activeMusicId === overlaySoundId) {
            return
        }

        if (this.activeMusicId) {
            // Keep suspendedMusicId intact so interruption chains (e.g. star collect
            // during tutorial pause) can still resume the level track afterward.
            this.pausedMusicId = this.activeMusicId
            this.fadeOutMusic(this.activeMusicId, fadeMs, 'pause')
        }

        this.musicRates.set(overlaySoundId, 1)
        this.fadeInMusic(overlaySoundId, fadeMs, false)
        this.activeMusicId = overlaySoundId
    }

    resumeMusicFromOverlay({ fadeMs = MUSIC_FADE_MS }: PlayMusicOptions = {}): void {
        const pausedId = this.pausedMusicId
        if (pausedId) {
            if (this.activeMusicId && this.activeMusicId !== pausedId) {
                this.fadeOutMusic(this.activeMusicId, fadeMs, 'stop')
            }

            this.fadeInMusic(pausedId, fadeMs, true)
            this.activeMusicId = pausedId
            this.pausedMusicId = null

            if (!this.musicShouldLoop(pausedId) && this.suspendedMusicId) {
                this.scheduleResumeWhenOverlayEnds(pausedId)
            }

            return
        }

        const resumeId = this.suspendedMusicId

        if (this.activeMusicId && this.activeMusicId !== resumeId) {
            this.fadeOutMusic(this.activeMusicId, fadeMs, 'stop')
        }

        if (!resumeId) {
            this.activeMusicId = null
            return
        }

        this.fadeInMusic(resumeId, fadeMs, true)
        this.activeMusicId = resumeId
        this.suspendedMusicId = null
    }

    playMusicInterruption(
        overlaySoundId: SoundId,
        { fadeMs = MUSIC_FADE_MS }: PlayMusicOptions = {},
    ): void {
        if (this.activeMusicId === overlaySoundId) {
            return
        }

        if (this.activeMusicId) {
            this.suspendedMusicId = this.activeMusicId
            this.fadeOutMusic(this.activeMusicId, fadeMs, 'pause')
        }

        this.musicRates.set(overlaySoundId, 1)
        this.fadeInMusic(overlaySoundId, fadeMs, false)
        this.activeMusicId = overlaySoundId

        if (!this.musicShouldLoop(overlaySoundId)) {
            this.scheduleResumeWhenOverlayEnds(overlaySoundId)
        }
    }

    getActiveMusicId(): SoundId | null {
        return this.activeMusicId
    }

    stopMusic(soundId?: SoundId, { fadeMs = MUSIC_FADE_MS }: PlayMusicOptions = {}): void {
        const id = soundId ?? this.activeMusicId
        if (!id) {
            return
        }

        this.fadeOutMusic(id, fadeMs, 'stop')

        if (this.activeMusicId === id) {
            this.activeMusicId = null
        }
        if (this.suspendedMusicId === id) {
            this.suspendedMusicId = null
        }
        if (this.pausedMusicId === id) {
            this.pausedMusicId = null
        }
    }

    private emitSoundPreloadChange(): void {
        const loaded = this.finishedPreloadSounds.size
        this.soundPreloadSnapshot = {
            started: this.soundPreloadStarted,
            ready: loaded >= TOTAL_SOUND_COUNT,
            loaded,
            total: TOTAL_SOUND_COUNT,
            progress: TOTAL_SOUND_COUNT === 0 ? 100 : (loaded / TOTAL_SOUND_COUNT) * 100,
        }

        for (const listener of this.soundPreloadListeners) {
            listener()
        }
    }

    private markSoundPreloadFinished(soundId: SoundId): void {
        if (this.finishedPreloadSounds.has(soundId)) {
            return
        }

        this.finishedPreloadSounds.add(soundId)
        this.emitSoundPreloadChange()
    }

    private clearMusicFadeTimer(soundId: SoundId): void {
        const timerId = this.musicFadeTimers.get(soundId)
        if (timerId !== undefined) {
            window.clearTimeout(timerId)
            this.musicFadeTimers.delete(soundId)
        }
    }

    private musicTargetVolume(soundId: SoundId): number {
        const definition = SOUNDS[soundId as keyof typeof SOUNDS] as
            | SoundDefinition
            | undefined
        return definition?.volume ?? DEFAULT_VOLUME
    }

    private musicShouldLoop(soundId: SoundId): boolean {
        const definition = SOUNDS[soundId as keyof typeof SOUNDS] as
            | SoundDefinition
            | undefined
        return definition?.loop ?? true
    }

    private fadeInMusic(soundId: SoundId, fadeMs: number, resume: boolean): void {
        const howl = this.getHowl(soundId)
        if (!howl) {
            return
        }

        this.clearMusicFadeTimer(soundId)

        const target = this.musicTargetVolume(soundId)
        const rate = Math.max(this.musicRates.get(soundId) ?? 1, 0.1)
        howl.loop(this.musicShouldLoop(soundId))

        const existingInstanceId = this.musicInstanceIds.get(soundId)

        if (resume && existingInstanceId !== undefined) {
            howl.rate(rate, existingInstanceId)
            howl.volume(0, existingInstanceId)
            if (!howl.playing(existingInstanceId)) {
                howl.play(existingInstanceId)
            }
            howl.fade(0, target, fadeMs, existingInstanceId)
            return
        }

        if (existingInstanceId !== undefined) {
            howl.stop(existingInstanceId)
        }

        const instanceId = howl.play()
        howl.rate(rate, instanceId)
        howl.volume(0, instanceId)
        howl.fade(0, target, fadeMs, instanceId)
        this.musicInstanceIds.set(soundId, instanceId)
    }

    private fadeOutMusic(soundId: SoundId, fadeMs: number, finish: 'stop' | 'pause'): void {
        const howl = this.howls.get(soundId)
        const instanceId = this.musicInstanceIds.get(soundId)
        if (!howl || instanceId === undefined) {
            return
        }

        this.clearMusicFadeTimer(soundId)

        if (!howl.playing(instanceId)) {
            if (finish === 'stop') {
                howl.stop(instanceId)
                this.musicInstanceIds.delete(soundId)
            } else {
                howl.pause(instanceId)
            }
            return
        }

        howl.fade(this.musicTargetVolume(soundId), 0, fadeMs, instanceId)

        const timerId = window.setTimeout(() => {
            if (finish === 'stop') {
                howl.stop(instanceId)
                this.musicInstanceIds.delete(soundId)
            } else {
                howl.pause(instanceId)
            }
            this.musicFadeTimers.delete(soundId)
        }, fadeMs)

        this.musicFadeTimers.set(soundId, timerId)
    }

    private scheduleResumeWhenOverlayEnds(overlaySoundId: SoundId): void {
        const howl = this.howls.get(overlaySoundId)
        const instanceId = this.musicInstanceIds.get(overlaySoundId)

        if (!howl || instanceId === undefined) {
            return
        }

        howl.once('end', () => {
            if (this.activeMusicId !== overlaySoundId) {
                return
            }

            this.resumeMusicFromOverlay()
        }, instanceId)
    }

    unloadSound(soundId: SoundId): void {
        const howl = this.howls.get(soundId)
        if (!howl) {
            return
        }

        howl.unload()
        this.howls.delete(soundId)
    }

    unloadAll(): void {
        for (const howl of this.howls.values()) {
            howl.unload()
        }
        this.howls.clear()
    }

    preloadSounds(): void {
        this.startSoundPreload()
    }

    private getHowl(soundId: SoundId): Howl | null {
        const definition = SOUNDS[soundId as keyof typeof SOUNDS] as
            | SoundDefinition
            | undefined

        if (!definition) {
            if (import.meta.env.DEV) {
                console.warn(`[soundPlayer] Unknown sound id: ${String(soundId)}`)
            }
            return null
        }

        const cached = this.howls.get(soundId)
        if (cached) {
            return cached
        }

        const howl = new Howl({
            src: normalizeSrc(definition.src),
            volume: definition.volume ?? DEFAULT_VOLUME,
            loop: definition.loop ?? false,
            html5: definition.html5 ?? false,
            preload: definition.preload ?? true,
        })

        howl.on('loaderror', (_id, error) => {
            if (import.meta.env.DEV) {
                console.warn(`[soundPlayer] Failed to load "${String(soundId)}":`, error)
            }
        })

        this.howls.set(soundId, howl)
        return howl
    }

    private attachUnlockListeners(): void {
        if (this.unlockListenersAttached || typeof window === 'undefined') {
            return
        }

        this.unlockListenersAttached = true

        const unlock = () => {
            void Howler.ctx?.resume()
        }

        window.addEventListener('pointerdown', unlock, { once: true })
        window.addEventListener('keydown', unlock, { once: true })
        window.addEventListener('touchstart', unlock, { once: true })
    }
}

export const soundPlayer = new SoundPlayer()

export const playSound = soundPlayer.playSound.bind(soundPlayer)
export const playSoundWithVolume = soundPlayer.playSoundWithVolume.bind(soundPlayer)
export const playSoundWithRate = soundPlayer.playSoundWithRate.bind(soundPlayer)
export const playSoundUntilFinished = soundPlayer.playSoundUntilFinished.bind(soundPlayer)
export const stopSound = soundPlayer.stopSound.bind(soundPlayer)
export const preloadSounds = soundPlayer.preloadSounds.bind(soundPlayer)
export const startSoundPreload = soundPlayer.startSoundPreload.bind(soundPlayer)
export const playMusic = soundPlayer.playMusic.bind(soundPlayer)
export const stopMusic = soundPlayer.stopMusic.bind(soundPlayer)
export const pauseMusicWithOverlay = soundPlayer.pauseMusicWithOverlay.bind(soundPlayer)
export const playMusicInterruption = soundPlayer.playMusicInterruption.bind(soundPlayer)
export const resumeMusicFromOverlay = soundPlayer.resumeMusicFromOverlay.bind(soundPlayer)
export const getActiveMusicId = soundPlayer.getActiveMusicId.bind(soundPlayer)

export function useSoundPreloadStatus(): SoundPreloadSnapshot {
    return useSyncExternalStore(
        soundPlayer.subscribeToSoundPreload.bind(soundPlayer),
        soundPlayer.getSoundPreloadSnapshot.bind(soundPlayer),
        soundPlayer.getSoundPreloadSnapshot.bind(soundPlayer),
    )
}
